import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import styles from './GpsExportModal.module.scss';
import Label from 'common/components/label/Label';
import Button from 'common/components/button/Button';
import FormItem from 'common/components/formItem/FormItem';
import DateTimePicker from 'common/components/dateTimePicker/DateTimePicker';
import InputGroup from 'common/components/inputGroup/InputGroup';
import InputError from 'common/components/inputError/InputError';
import { FaCalendarAlt, FaListUl, FaRoute } from 'react-icons/fa';
import { SpinnerCircular } from 'spinners-react';
import { DATEPICKER_FORMAT_DEFAULT, DEFAULT_LANGUAGE, STORAGE } from 'Config';
import Storage from 'common/services/Storage';
import moment from 'moment';
import { useToasts } from 'react-toast-notifications';
import TrackingService from 'api/tracking/TrackingService';
import FileSaver from 'file-saver';

type ExportType = 'summary' | 'detailed';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    vehicleId: string;
    daysWithRoutes: Date[];
};

const GpsExportModal: React.FC<Props> = ({ isOpen, onClose, vehicleId, daysWithRoutes }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const locale = Storage.get(STORAGE.CURRENT_LOCALE) || DEFAULT_LANGUAGE;

    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [exportType, setExportType] = useState<ExportType>('summary');
    const [showDateError, setShowDateError] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [showProgressBar, setShowProgressBar] = useState(false);
    const [progress, setProgress] = useState(0);
    const [totalTrips, setTotalTrips] = useState(0);
    const [processedTrips, setProcessedTrips] = useState(0);

    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const animationIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const progressBarDelayRef = useRef<NodeJS.Timeout | null>(null);
    const currentJobIdRef = useRef<string | null>(null);
    const serverProgressRef = useRef<number>(0);

    const customStyles = {
        content: {
            top: '5%',
            left: '25%',
            right: '25%',
            bottom: 'auto',
            backgroundClip: 'padding-box',
            backgroundColor: '#FFFFFF',
            border: '1px solid transparent',
            borderRadius: '4px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
            outline: '0 none',
            padding: '1.5em',
            overflow: 'visible'
        },
        overlay: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            flex: 1,
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            if (animationIntervalRef.current) {
                clearInterval(animationIntervalRef.current);
                animationIntervalRef.current = null;
            }
            if (progressBarDelayRef.current) {
                clearTimeout(progressBarDelayRef.current);
                progressBarDelayRef.current = null;
            }
        };
    }, []);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            if (animationIntervalRef.current) {
                clearInterval(animationIntervalRef.current);
                animationIntervalRef.current = null;
            }
            if (progressBarDelayRef.current) {
                clearTimeout(progressBarDelayRef.current);
                progressBarDelayRef.current = null;
            }
            setIsExporting(false);
            setShowProgressBar(false);
            setProgress(0);
            setTotalTrips(0);
            setProcessedTrips(0);
            currentJobIdRef.current = null;
            serverProgressRef.current = 0;
        }
    }, [isOpen]);

    const handleExport = async () => {
        if (!startDate && !endDate) {
            setShowDateError(true);
            return;
        }

        if (startDate && endDate && startDate > endDate) {
            addToast(t('location.export_modal.invalid_date_range'), { appearance: 'warning' });
            return;
        }

        setShowDateError(false);
        setIsExporting(true);
        setShowProgressBar(false);
        setProgress(0);
        setTotalTrips(0);
        setProcessedTrips(0);
        serverProgressRef.current = 0;

        // After 5 seconds, show progress bar with smooth animation to server progress
        progressBarDelayRef.current = setTimeout(() => {
            setProgress(0);
            setShowProgressBar(true);
            if (!animationIntervalRef.current) {
                animationIntervalRef.current = setInterval(() => {
                    setProgress(prev => {
                        const target = serverProgressRef.current;

                        // If we've reached the target, stop
                        if (prev >= target) {
                            return prev;
                        }

                        // Smooth linear interpolation toward target from server
                        const diff = target - prev;
                        const increment = Math.min(0.5, diff); // Max 0.5% per 100ms = 5% per second
                        const newProgress = prev + increment;
                        return Math.round(newProgress * 10) / 10; // Round to 1 decimal to prevent accumulation
                    });
                }, 100);
            }
        }, 5000);

        try {
            // Start export job
            const response = await TrackingService.startExportGpsRoutes(vehicleId, startDate, endDate, exportType);
            const jobId = response.jobId;
            currentJobIdRef.current = jobId;

            // Poll server every 5 seconds to reduce network traffic
            pollingIntervalRef.current = setInterval(async () => {
                try {
                    const progressData = await TrackingService.getExportProgress(jobId);
                    serverProgressRef.current = progressData.progress;
                    if (progressData.totalTrips && progressData.totalTrips > 0) {
                        setTotalTrips(progressData.totalTrips);
                    }
                    if (progressData.processedTrips !== undefined) {
                        setProcessedTrips(progressData.processedTrips);
                    }

                    if (progressData.status === 'complete') {
                        // Stop all intervals and timeouts
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current);
                            pollingIntervalRef.current = null;
                        }
                        if (animationIntervalRef.current) {
                            clearInterval(animationIntervalRef.current);
                            animationIntervalRef.current = null;
                        }
                        if (progressBarDelayRef.current) {
                            clearTimeout(progressBarDelayRef.current);
                            progressBarDelayRef.current = null;
                        }

                        setProgress(100);

                        // Download the file
                        const file = await TrackingService.downloadExportFile(jobId);
                        const routesName = t('location.export_modal.filename_routes');
                        const typeName = exportType === 'summary' ? t('location.export_modal.filename_summary') : t('location.export_modal.filename_detailed');

                        // Build filename with dates only if they exist
                        let filename = `${routesName}_${typeName}`;
                        if (startDate) {
                            filename += `_${moment(startDate).format('DD-MM-YYYY')}`;
                        }
                        if (endDate) {
                            filename += `_${moment(endDate).format('DD-MM-YYYY')}`;
                        }
                        filename += '.xlsx';
                        FileSaver.saveAs(file, filename);

                        setIsExporting(false);
                        setShowProgressBar(false);
                        addToast(t('location.export_modal.export_success'), { appearance: 'success' });
                        onClose();
                    } else if (progressData.status === 'error') {
                        // Stop all intervals and timeouts
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current);
                            pollingIntervalRef.current = null;
                        }
                        if (animationIntervalRef.current) {
                            clearInterval(animationIntervalRef.current);
                            animationIntervalRef.current = null;
                        }
                        if (progressBarDelayRef.current) {
                            clearTimeout(progressBarDelayRef.current);
                            progressBarDelayRef.current = null;
                        }
                        setIsExporting(false);
                        setShowProgressBar(false);
                        addToast(progressData.message || t('location.export_modal.export_error'), { appearance: 'error' });
                    }
                } catch (pollError) {
                    console.error('Error polling progress:', pollError);
                }
            }, 5000); // Poll every 5 seconds

        } catch (error) {
            console.error('Export error:', error);
            if (progressBarDelayRef.current) {
                clearTimeout(progressBarDelayRef.current);
                progressBarDelayRef.current = null;
            }
            setIsExporting(false);
            setShowProgressBar(false);
            addToast(t('location.export_modal.export_error'), { appearance: 'error' });
        }
    };

    const handleCancel = () => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        if (animationIntervalRef.current) {
            clearInterval(animationIntervalRef.current);
            animationIntervalRef.current = null;
        }
        if (progressBarDelayRef.current) {
            clearTimeout(progressBarDelayRef.current);
            progressBarDelayRef.current = null;
        }
        setIsExporting(false);
        setShowProgressBar(false);
        setProgress(0);
        currentJobIdRef.current = null;
        serverProgressRef.current = 0;
        onClose();
    };

    const isSameDay = (d1: Date, d2: Date) =>
        d1.getDate() === d2.getDate() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getFullYear() === d2.getFullYear();

    const renderDayContents = (day: number, date: Date) => {
        const isSpecial = daysWithRoutes.some(d => isSameDay(d, date));
        return (
            <div style={{ color: isSpecial ? '#16E0CC' : undefined }}>
                <span>{day}</span>
            </div>
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            style={customStyles}
            onRequestClose={isExporting ? undefined : onClose}
        >
            <div className={styles.titleContainer}>
                <Label className={styles.textTitle}>{t('location.export_modal.title')}</Label>
            </div>

            {isExporting ? (
                <div className={styles.exportingContainer}>
                    <div className={styles.spinnerWrapper}>
                        <SpinnerCircular
                            size={60}
                            thickness={120}
                            speed={100}
                            color="#1781A1"
                            secondaryColor="rgba(23, 129, 161, 0.2)"
                        />
                    </div>
                    <Label className={styles.exportingTitle}>{t('location.export_modal.exporting')}</Label>
                    {showProgressBar && (
                        <>
                            <div className={styles.progressBar}>
                                <div
                                    className={styles.progressFill}
                                    style={{ width: `${Math.round(progress)}%` }}
                                />
                            </div>
                            <Label className={styles.progressText}>{Math.round(progress)}%</Label>
                            <Label className={styles.exportingMessage}>
                                {t('location.export_modal.may_take_minutes')}
                            </Label>
                            {totalTrips > 0 && (
                                <Label className={styles.tripsCount}>
                                    {t('location.export_modal.trips_progress', { processed: processedTrips, total: totalTrips })}
                                </Label>
                            )}
                        </>
                    )}
                </div>
            ) : (
                    <>
                        <div className={styles.section}>
                            <Label className={styles.label}>{t('location.export_modal.export_type')}</Label>
                            <div className={styles.exportTypeContainer}>
                                <div
                                    className={`${styles.exportTypeOption} ${exportType === 'summary' ? styles.selected : ''}`}
                                    onClick={() => setExportType('summary')}
                                >
                                    <div className={styles.exportTypeIcon}>
                                        <FaListUl />
                                    </div>
                                    <div className={styles.exportTypeContent}>
                                        <div className={styles.exportTypeTitle}>{t('location.export_modal.type_summary')}</div>
                                        <div className={styles.exportTypeDescription}>{t('location.export_modal.type_summary_description')}</div>
                                    </div>
                                </div>
                                <div
                                    className={`${styles.exportTypeOption} ${exportType === 'detailed' ? styles.selected : ''}`}
                                    onClick={() => setExportType('detailed')}
                                >
                                    <div className={styles.exportTypeIcon}>
                                        <FaRoute />
                                    </div>
                                    <div className={styles.exportTypeContent}>
                                        <div className={styles.exportTypeTitle}>{t('location.export_modal.type_detailed')}</div>
                                        <div className={styles.exportTypeDescription}>{t('location.export_modal.type_detailed_description')}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={styles.section}>
                            <Label className={styles.label}>{t('car_verification.trip_filter_date')} *</Label>
                            <div className={styles.datePickersRow}>
                                <div className={styles.datePickerCol}>
                                    <FormItem style={{ marginBottom: 0 }}>
                                        <DateTimePicker
                                            renderDayContents={renderDayContents}
                                            name="startDate"
                                            dateFormat={DATEPICKER_FORMAT_DEFAULT}
                                            locale={locale}
                                            autoComplete='off'
                                            selected={startDate}
                                            placeholderText={t('location.date_start')}
                                            onChange={(date: Date) => setStartDate(date)}
                                            maxDate={endDate || new Date()}
                                            customInput={<InputGroup icon={<FaCalendarAlt />} />}
                                            popperClassName={styles.datePickerPopper}
                                        />
                                    </FormItem>
                                </div>
                                <div className={styles.datePickerCol}>
                                    <FormItem style={{ marginBottom: 0 }}>
                                        <DateTimePicker
                                            renderDayContents={renderDayContents}
                                            name="endDate"
                                            dateFormat={DATEPICKER_FORMAT_DEFAULT}
                                            locale={locale}
                                            autoComplete='off'
                                            selected={endDate}
                                            placeholderText={t('location.date_end')}
                                            onChange={(date: Date) => setEndDate(date)}
                                            minDate={startDate || undefined}
                                            maxDate={new Date()}
                                            customInput={<InputGroup icon={<FaCalendarAlt />} />}
                                            popperClassName={styles.datePickerPopper}
                                        />
                                    </FormItem>
                                </div>
                            </div>
                            <div className={styles.dateError}>
                                <InputError error={showDateError && !startDate && !endDate ? { type: 'required', message: t('location.export_modal.select_at_least_one_date') } : undefined} />
                            </div>
                        </div>
                    </>
                )}

            <div className={styles.buttons_container}>
                <Button
                    text={t('common.cancel')}
                    preset={'secondary'}
                    size={'normal'}
                    onClick={handleCancel}
                />
                {!isExporting && (
                    <Button
                        text={t('location.export_modal.export')}
                        size={'normal'}
                        onClick={handleExport}
                    />
                )}
            </div>
        </Modal>
    );
};

export default GpsExportModal;
